<template>
  <div class="generated-creative">
    <h3 class="section-title">
      Original & AI Images
    </h3>

    <div
      v-if="images.length > 0"
      class="waterfall-container"
    >
      <div
        v-for="(image, index) in images"
        :key="image.id || index"
        class="waterfall-column"
      >
        <div
          class="creative-card"
          @mouseenter="setActive(index, true)"
          @mouseleave="setActive(index, false)"
        >
          <div class="creative-card__media">
            <img
              :src="image.url"
              alt="Generated creative"
            >
            <span
              v-if="image.badgeLabel"
              class="badge"
              :class="{ 'generating-badge': image.isGenerating }"
            >
              {{ image.badgeLabel }}
            </span>

            <!-- Loading spinner for generating cards -->
            <div
              v-if="image.isGenerating"
              class="creative-card__spinner"
            >
              <div class="creative-card__spinner-circle" />
            </div>
          </div>

          <transition
            v-if="!image.isGenerating"
            name="fade-slide"
          >
            <div
              v-if="activeIndex === index || image.isGeneratingVideo || image.isLoadingPrompt"
              class="hover-panel"
            >
              <h4 class="panel-title">
                Image to video Prompt
              </h4>
              <b-form-textarea
                v-model="image.prompt"
                :maxlength="promptLimit"
                :disabled="image.isGeneratingVideo"
                rows="4"
                class="panel-textarea"
                placeholder="Describe your video, upload a reference, or pick from inspiration."
              />
              <div class="panel-footer">
                <b-button
                  variant="primary"
                  class="generate-button"
                  :disabled="image.isLoadingPrompt || image.isGeneratingVideo || !image.prompt"
                  @click="handleGenerateVideo(image)"
                >
                  <feather-icon
                    :icon="image.isGeneratingVideo ? 'LoaderIcon' : 'SparklesIcon'"
                    size="16"
                    :class="{ 'spinning': image.isGeneratingVideo }"
                  />
                  <span>{{ image.isGeneratingVideo || image.isLoadingPrompt ? 'Generating…' : 'Generate' }}</span>
                </b-button>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>

    <div v-else>
      <div class="empty-state">
        <feather-icon
icon="ImageIcon"
size="24"
/>
        <span>No images generated</span>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BFormTextarea } from 'bootstrap-vue'
import { mapActions } from 'vuex'

export default {
  name: 'ImageGeneratedCreative',
  components: {
    BButton,
    BFormTextarea,
  },
  props: {
    images: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      activeIndex: -1,
      promptLimit: 1000,
    }
  },
  computed: {
    // No computed properties needed for grid layout
  },
  watch: {
    // Watch for changes in images array to trigger prompt generation
    images: {
      immediate: true,
      handler(newImages) {
        newImages.forEach(image => {
          if (image.url && !image.prompt) {
            this.fetchPromptForImage(image)
          }
        })
      },
    },
  },
  beforeDestroy() {
    // Clean up all polling intervals
    this.images.forEach(image => {
      this.stopVideoPolling(image)
    })
  },
  methods: {
    ...mapActions('videoGeneration', ['generateImagePrompt', 'generateVideo', 'pollVideoStatus']),
    setActive(index, state) {
      this.activeIndex = state ? index : -1
    },
    async handleGenerateVideo(image) {
      const payload = {
        imageUrl: image.url,
        prompt: image.prompt?.trim(),
      }

      try {
        // Update the image's generating state
        this.$set(image, 'isGeneratingVideo', true)
        const response = await this.generateVideo(payload)

        // Extract task ID for polling
        const taskId = response?.data?.[0]?.taskId || ''

        if (taskId) {
          // Store task ID in the image for polling tracking
          this.$set(image, 'videoTaskId', taskId)
        } else {
          // If no task ID, stop generating state
          this.$set(image, 'isGeneratingVideo', false)
          const errorMessage = response?.error?.data?.message || 'Failed to start video generation'
          this.$toast.error(errorMessage)
          return
        }

        this.$emit('generate-video', {
          ...payload,
          response,
          video: response?.data?.video || response?.data?.[0] || response?.video,
          taskId,
        })
      } catch (error) {
        const errorMessage = error?.data?.message || 'Failed to start video generation'
        // Only stop generating state on error
        this.$set(image, 'isGeneratingVideo', false)
        this.$toast.error(errorMessage)
      }
    },
    async fetchPromptForImage(image) {
      if (!image.url) return
      try {
        this.$set(image, 'isLoadingPrompt', true)
        const response = await this.generateImagePrompt({ imageUrl: image.url })
        const promptText = response?.data?.prompt || response?.data || response?.prompt
        this.$set(image, 'prompt', String(promptText || '').slice(0, this.promptLimit))
      } catch (error) {
        this.$toast.error('Failed to generate prompt')
      } finally {
        this.$set(image, 'isLoadingPrompt', false)
      }
    },
    stopVideoPolling(image) {
      if (image.pollingInterval) {
        clearInterval(image.pollingInterval)
        this.$set(image, 'pollingInterval', null)
      }
    },
    resetGeneratingState(imageId) {
      const image = this.images.find(img => img.id === imageId)
      if (image) {
        this.$set(image, 'isGeneratingVideo', false)
        this.$set(image, 'isLoadingPrompt', false)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.generated-creative {
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 32px;
  background: #ffffff;
  padding: 24px 32px 32px;
  border-radius: 32px;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.08);
}

.section-title {
  font-size: 22px;
  font-weight: 600;
  color: #1f2a4d;
  margin: 0;
}

.creative-card {
  position: relative;
  width: 100%;
  border-radius: 24px;
  background: #ffffff;
  overflow: hidden;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08);
}

.badge {
  position: absolute;
  top: 16px;
  right: 16px;
  background: rgba(184, 134, 11, 0.92);
  color: #fff;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.generating-badge {
  background: #007bff;
  color: #ffffff;
}

.hover-panel {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 16px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.9) 45%, #ffffff 100%);
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
}

.panel-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
}

.panel-textarea {
  font-size: 14px;
  color: #1f2937;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(148, 163, 184, 0.4);
  border-radius: 16px;
  padding: 12px 14px;
  resize: none;
  box-shadow: inset 0 1px 1px rgba(15, 23, 42, 0.05);

  &:focus {
    border-color: #a855f7;
    box-shadow: 0 0 0 3px rgba(168, 85, 247, 0.18);
  }

  &:disabled {
    background: rgba(243, 244, 246, 0.94);
    color: #9ca3af;
    cursor: not-allowed;
    opacity: 0.7;
  }
}

.panel-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  text-align: center;

  .btn {
    width: 100%;
    margin: 0 auto;
  }
}

.generate-button {
  gap: 8px;
  border-radius: 999px;
  padding: 10px 18px;
  border: none;
  background: linear-gradient(135deg, #60a5fa 0%, #a855f7 50%, #f472b6 100%);
  color: #ffffff;
  font-weight: 600;

  &:hover,
  &:focus {
    background: linear-gradient(135deg, #3b82f6 0%, #9333ea 50%, #ec4899 100%);
  }
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.fade-slide-enter,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
